import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, MapPin, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { translateWorldTitle, translateWorldSubtitle } from "../lib/journeyLabels";

// Per-world backdrop photos (mirrors Journey.jsx WORLD_BG).
const WORLD_BG = {
  couch_to_active: "https://images.unsplash.com/photo-1765432424043-35be7717f0de?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  "5k_training": "https://images.unsplash.com/photo-1502904550040-7534597429ae?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  marathon_prep: "https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  boot_camp: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  iron_will: "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
};

/**
 * One-time "New trail unlocked" celebration shown when finishing a level
 * opens the next world on the Journey map. Styled to match the other
 * celebration overlays (dark gym gradient card, spring-in, sparkles),
 * with the newly-unlocked world's photo banner + title/subtitle.
 * Auto-dismisses after ~3.6s; tap anywhere to close early.
 *
 * Props: open, world {world_id, title, subtitle}, onDone
 */
export default function WorldUnlockCelebration({ open, world, onDone }) {
  const { t } = useTranslation();
  const onDoneRef = React.useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => onDoneRef.current?.(), 3600);
    return () => clearTimeout(timer);
  }, [open]);

  const bg = world ? WORLD_BG[world.world_id] : null;

  return (
    <AnimatePresence>
      {open && world && (
        <div
          data-testid="world-unlock-celebration"
          className="fixed inset-0 z-[210] flex items-center justify-center px-4 bg-[#2C332A]/55 backdrop-blur-[3px]"
          aria-live="polite"
          onClick={() => onDoneRef.current?.()}
        >
          <motion.div
            initial={{ opacity: 0, y: 34, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="relative w-full max-w-[350px] rounded-3xl overflow-hidden shadow-2xl text-center"
            style={{
              background: "linear-gradient(160deg,#3A473B 0%,#2C332A 100%)",
              boxShadow: "0 24px 70px rgba(20,24,19,0.6), inset 0 2px 0 rgba(255,255,255,0.18)",
            }}
          >
            {/* World photo banner with unlock reveal */}
            <div className="relative h-36 w-full overflow-hidden">
              {bg && (
                <motion.div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${bg})` }}
                  initial={{ scale: 1.25, filter: "grayscale(1) brightness(0.5)" }}
                  animate={{ scale: 1, filter: "grayscale(0) brightness(1)" }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                />
              )}
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(44,51,42,0.15) 0%, rgba(44,51,42,0.85) 100%)" }} />
              {/* broken-lock icon that pops then fades */}
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: [0.4, 1.15, 1], opacity: [0, 1, 0] }}
                transition={{ duration: 1.4, times: [0, 0.4, 1] }}
                className="absolute inset-0 grid place-items-center text-white/90 pointer-events-none"
              >
                <Lock size={40} strokeWidth={1.6} />
              </motion.div>
            </div>

            <div className="relative px-6 pt-4 pb-7">
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                  animate={{ opacity: [0, 1, 0], x: (i % 2 === 0 ? -1 : 1) * (34 + i * 15), y: -18 - i * 12, scale: 1, rotate: 28 * (i - 2) }}
                  transition={{ duration: 1.7, delay: 0.2 + i * 0.12 }}
                  className="absolute -top-2 left-1/2 -translate-x-1/2 text-[#E8A365] pointer-events-none"
                >
                  <Sparkles size={16} strokeWidth={2.2} />
                </motion.div>
              ))}

              <p className="relative text-[11px] uppercase tracking-[0.22em] font-bold text-[#E8A365] mb-2 inline-flex items-center gap-1.5">
                <MapPin size={12} strokeWidth={2.2} />
                {t("journey.world_unlock.tag", "New trail unlocked")}
              </p>
              <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="relative text-2xl font-semibold text-white leading-tight">
                {translateWorldTitle(world, t)}
              </h2>
              <p className="relative text-xs text-white/70 mt-1">
                {translateWorldSubtitle(world, t)}
              </p>
              <p className="relative text-[13px] text-white/85 mt-4 leading-snug">
                {t("journey.world_unlock.body", "A whole new set of challenges just opened up on your journey. Keep the momentum going!")}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
